const dayKey = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const dayNumber = (value) => Date.parse(`${value}T12:00:00Z`) / 86400000;
const dueDay = (activity) => {
  if (activity.effectiveDueDate) return dayKey(activity.effectiveDueDate);
  const planned = dayKey(activity.plannedDate);
  if (!planned) return "";
  const date = new Date(`${planned}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + (Number(activity.areaDelayDays) || 0));
  return date.toISOString().slice(0, 10);
};
const completedStatus = (status) => ["finished", "closed", "completed", "completed_with_issue"].includes(status);

export function residentDashboardData(orders = [], activities = [], range = "Este mês", now = new Date(), customRange = {}) {
  const today = dayKey(now);
  const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === "Esta semana") startDate.setDate(startDate.getDate() - 6);
  else if (range === "Mês anterior") startDate.setFullYear(now.getFullYear(), now.getMonth() - 1, 1);
  else if (range === "Próximo mês") startDate.setFullYear(now.getFullYear(), now.getMonth() + 1, 1);
  else if (range === "Este ano") startDate.setFullYear(now.getFullYear(), 0, 1);
  else if (range !== "Hoje") startDate.setDate(1);
  const start = range === "Período selecionado" ? customRange.start || today : dayKey(startDate);
  const endDate = range === "Mês anterior" ? new Date(now.getFullYear(), now.getMonth(), 0) : range === "Próximo mês" ? new Date(now.getFullYear(), now.getMonth() + 2, 0) : range === "Este ano" ? new Date(now.getFullYear(), 11, 31) : range === "Este mês" ? new Date(now.getFullYear(), now.getMonth() + 1, 0) : now;
  const end = range === "Período selecionado" ? customRange.end || start : dayKey(endDate);
  const inPeriod = (value) => { const key = dayKey(value); return !!key && key >= start && key <= end; };
  const customDays = range === "Período selecionado" ? Math.max(1, dayNumber(end) - dayNumber(start) + 1) : 0;
  const customBucketDays = customDays <= 14 ? 1 : customDays <= 93 ? 7 : 31;
  const buckets = range === "Período selecionado"
    ? Array.from({ length: Math.ceil(customDays / customBucketDays) }, (_, index) => {
        const first = new Date(`${start}T12:00:00Z`);
        first.setUTCDate(first.getUTCDate() + index * customBucketDays);
        const last = new Date(first);
        last.setUTCDate(first.getUTCDate() + Math.min(customBucketDays - 1, dayNumber(end) - dayNumber(dayKey(first))));
        return { label: customBucketDays === 1 ? `${String(first.getUTCDate()).padStart(2, "0")}/${String(first.getUTCMonth() + 1).padStart(2, "0")}` : `${String(first.getUTCDate()).padStart(2, "0")}/${String(first.getUTCMonth() + 1).padStart(2, "0")}–${String(last.getUTCDate()).padStart(2, "0")}/${String(last.getUTCMonth() + 1).padStart(2, "0")}`, created: 0, completed: 0, planned: 0, realized: 0 };
      })
    : range === "Hoje"
    ? Array.from({ length: 4 }, (_, index) => ({ label: `${index * 6}–${index * 6 + 5}h`, created: 0, completed: 0, planned: 0, realized: 0 }))
    : range === "Esta semana"
      ? Array.from({ length: 7 }, (_, index) => { const date = new Date(startDate); date.setDate(date.getDate() + index); return { label: `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`, key: dayKey(date), created: 0, completed: 0, planned: 0, realized: 0 }; })
      : range === "Este ano"
        ? Array.from({ length: 12 }, (_, index) => ({ label: new Intl.DateTimeFormat("pt-BR", { month: "short" }).format(new Date(now.getFullYear(), index, 1)), created: 0, completed: 0, planned: 0, realized: 0 }))
        : Array.from({ length: Math.ceil(endDate.getDate() / 7) }, (_, index) => ({ label: `${index * 7 + 1}–${Math.min(endDate.getDate(), index * 7 + 7)}`, created: 0, completed: 0, planned: 0, realized: 0 }));
  const bucketIndex = (value) => {
    if (!inPeriod(value)) return -1;
    if (range === "Hoje") return /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? 0 : Math.floor(new Date(value).getHours() / 6);
    if (range === "Período selecionado") return Math.floor((dayNumber(dayKey(value)) - dayNumber(start)) / customBucketDays);
    if (range === "Esta semana") return buckets.findIndex((bucket) => bucket.key === dayKey(value));
    if (range === "Este ano") return Number(dayKey(value).slice(5, 7)) - 1;
    return Math.floor((Number(dayKey(value).slice(8, 10)) - 1) / 7);
  };
  const increment = (value, field) => { const index = bucketIndex(value); if (index >= 0 && buckets[index]) buckets[index][field] += 1; };
  for (const order of orders) {
    increment(order.createdAt, "created");
    if (completedStatus(order.rawStatus || order.status)) increment(order.finishedAt, "completed");
  }
  for (const activity of activities) {
    if (["cancelled", "waiting_area_release"].includes(activity.status)) continue;
    increment(activity.plannedDate, "planned");
    if (completedStatus(activity.status)) increment(activity.completedAt, "realized");
  }
  const assessed = activities.filter((activity) => {
    if (["cancelled", "waiting_area_release"].includes(activity.status) || !activity.plannedDate) return false;
    if (completedStatus(activity.status)) return inPeriod(activity.completedAt);
    return inPeriod(activity.plannedDate) && dueDay(activity) < today;
  });
  let onTime = 0, completedLate = 0, overdueOpen = 0;
  const totalScore = assessed.reduce((sum, activity) => {
    const completed = completedStatus(activity.status);
    const observedDay = completed ? dayKey(activity.completedAt) : today;
    const lateDays = Math.max(0, dayNumber(observedDay) - dayNumber(dueDay(activity)));
    if (completed && lateDays === 0) onTime += 1;
    else if (completed) completedLate += 1;
    else overdueOpen += 1;
    return sum + Math.max(0, 10 - lateDays * 2);
  }, 0);
  return {
    buckets,
    period: { start, end },
    orders: { created: buckets.reduce((sum, item) => sum + item.created, 0), completed: buckets.reduce((sum, item) => sum + item.completed, 0) },
    preventive: { planned: buckets.reduce((sum, item) => sum + item.planned, 0), realized: buckets.reduce((sum, item) => sum + item.realized, 0), onTime, completedLate, overdueOpen, assessed: assessed.length, score: assessed.length ? Math.round(totalScore / assessed.length * 10) / 10 : null },
  };
}

export function residentActivityChartData(orders = [], activities = [], days = 7, now = new Date()) {
  const count = Math.max(1, Math.min(31, Number(days) || 7));
  const first = new Date(now.getFullYear(), now.getMonth(), now.getDate() - count + 1);
  const buckets = Array.from({ length: count }, (_, index) => {
    const date = new Date(first.getFullYear(), first.getMonth(), first.getDate() + index);
    return { key: dayKey(date), label: new Intl.DateTimeFormat("pt-BR", { weekday: "short" }).format(date).replace(".", ""), day: date.getDate(), preventive: 0, corrective: 0 };
  });
  const byDay = new Map(buckets.map((bucket) => [bucket.key, bucket]));
  for (const order of orders) {
    if (!completedStatus(order.rawStatus || order.status)) continue;
    const bucket = byDay.get(dayKey(order.finishedAt));
    if (bucket) bucket.corrective += 1;
  }
  for (const activity of activities) {
    if (!completedStatus(activity.status)) continue;
    const bucket = byDay.get(dayKey(activity.completedAt));
    if (bucket) bucket.preventive += 1;
  }
  return buckets;
}
